import type { ReactNode } from 'react';
import { Activity, Globe, HardDrive, TriangleAlert } from 'lucide-react';
import { Banner, PageHeader } from '@/components/ui';
import { AlertsCard } from '@/components/dashboard/AlertsCard';
import { AttentionCard } from '@/components/dashboard/AttentionCard';
import { Metric } from '@/components/dashboard/Metric';
import { ProtectionCard } from '@/components/dashboard/ProtectionCard';
import { RecentJobsCard } from '@/components/dashboard/RecentJobsCard';
import { useNodes } from '@/hooks/useNodes';
import { useOverview, useSystemSettings } from '@/hooks/useSystem';
import { panelBackupStale } from '@/utils/backups';
import { formatRelative } from '@/utils/format';

/**
 * The first screen.
 *
 * Four numbers and two short lists, chosen to answer "is anything wrong" in one
 * glance. Anything that needs a second glance belongs on the page that owns it,
 * which is why each block here links somewhere rather than growing.
 */
export function DashboardPage(): ReactNode {
  // Counted by the server over everything, not by the browser over the first
  // page: a dashboard that stops counting at fifty is wrong when it is busiest.
  const overview = useOverview();
  const nodes = useNodes({ pollMs: 15000 });
  const settings = useSystemSettings();

  const summary = overview.data;
  const running = summary?.jobs.active ?? 0;
  const failed = summary?.jobs.failedLastDay ?? 0;
  const offline = nodes.data?.filter((node) => node.stale && node.agentVersion !== null).length ?? 0;
  const enrolled = nodes.data?.filter((node) => node.agentVersion !== null).length ?? 0;

  return (
    <div className="page">
      <PageHeader title="Обзор" />

      {/* ADR-0013: until backups are with an independent provider, losing the
          panel's server loses its copies too. Said for as long as it is true. */}
      {settings.data && !settings.data.offsite ? (
        <Banner tone="warning" icon={TriangleAlert}>
          Внешнее хранилище копий не подключено: копии хранятся только на серверах сайтов и панели.
        </Banner>
      ) : null}

      {/* The panel's own database: without it the sites keep serving, but
          nothing can be managed and no key opens what was lost. */}
      {settings.data && panelBackupStale(settings.data.panelBackup) ? (
        <Banner tone="warning" icon={TriangleAlert}>
          {settings.data.panelBackup
            ? `Последняя копия базы панели — ${formatRelative(settings.data.panelBackup.at)}. Проверьте воркер и хранилище.`
            : 'Копии базы панели ещё нет. Она создаётся раз в сутки после запуска воркера.'}
        </Banner>
      ) : null}

      <div className="grid-cards">
        <Metric icon={Globe} label="Сайтов" value={overview.isPending ? null : String(summary?.sites.total ?? 0)} to="/sites" />
        <Metric
          icon={HardDrive}
          label="Серверов в работе"
          value={nodes.isPending ? null : String(enrolled - offline)}
          {...(offline > 0 ? { tone: 'tone-degraded' } : {})}
          to="/servers"
        />
        <Metric icon={Activity} label="Задач в работе" value={overview.isPending ? null : String(running)} to="/jobs" />
        <Metric
          icon={TriangleAlert}
          label="Ошибок за сутки"
          value={overview.isPending ? null : String(failed)}
          {...(failed > 0 ? { tone: 'tone-down' } : {})}
          to="/jobs"
        />
      </div>

      {summary ? <AlertsCard alerts={summary.alerts} /> : null}

      <ProtectionCard overview={overview.isPending ? undefined : (summary ?? null)} />

      <div className="split">
        <AttentionCard overview={overview.isPending ? undefined : (summary ?? null)} />
        <RecentJobsCard />
      </div>
    </div>
  );
}
